import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import * as dropin from 'braintree-web-drop-in';
import { NgxSpinnerService } from 'ngx-spinner';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { ProfessionalService } from 'src/app/services/professional/professional.service';
import { RealtorService } from 'src/app/services/realtors/realtor.service';
import { CommonConstants } from 'src/app/constants/commonConstants';
import { PaymentModel, RealtorPlan } from 'src/app/models/PaymentModel';

/**
 * Consumer upgrade - the domiiRescue membership payment page.
 *
 * Reached from the Upgrade Membership popup (app) or the dashboard redirect (web) with the
 * consumer's UpgradeMembership code, the same code the realtor upgrade page takes, so it pays
 * through the existing V3/payment/update pipeline. Like that page it needs no login: the code
 * identifies the person.
 *
 * A separate page rather than a branch inside UpgradePaymentComponent, which is realtor-only
 * throughout (zip codes, plan categories, promo rules, the licensed-agent notice).
 *
 * Shows only the paid ConsumerUpgrade plan: the consumer is already on the free one.
 */
@Component({
	selector: 'app-consumer-payment',
	templateUrl: './consumer-payment.component.html',
	styleUrls: ['../upgrade-payment/upgrade-payment.component.scss', './consumer-payment.component.scss']
})
export class ConsumerPaymentComponent implements OnInit, OnDestroy {

	paymentForm: FormGroup;
	submitted = false;
	xClientId: string;
	upgradeCode = '';
	plans: RealtorPlan[] = [];
	selectedPlan: RealtorPlan | null = null;
	dropInInstance: dropin.Dropin | null = null;
	isCardFormOpen = false;
	errorMessage = '';
	errorHasButton = false;
	successMessage = '';
	tcURL: SafeResourceUrl | null = null;
	tcHeader = '';
	iOSStoreUrl: string;
	androidStoreUrl: string;
	/**
	 * Arrived straight from consumer signup on a paid invite (?signup=1). The account already exists
	 * on the free plan, so the page says so and offers to continue without paying.
	 */
	fromSignup = false;

	constructor(
		private formBuilder: FormBuilder,
		private realtorService: RealtorService,
		private professionalService: ProfessionalService,
		private environmentService: EnvironmentService,
		private spinner: NgxSpinnerService,
		private route: ActivatedRoute,
		private sanitizer: DomSanitizer,
	) {
		this.upgradeCode = this.route.snapshot.params['code'];
		this.fromSignup = this.route.snapshot.queryParamMap.get('signup') === '1';
		this.xClientId = this.environmentService.getXClientId();
	}

	ngOnInit(): void {
		this.paymentForm = this.formBuilder.group({
			membershipPlan: new FormControl('', [Validators.required]),
			creditCard: new FormControl(false, [Validators.requiredTrue]),
			agreeTandC: new FormControl(false, [Validators.requiredTrue]),
		});
		// AC-4664: the consumer pays for RescueMII, so the badges open RescueMII's listings.
		this.iOSStoreUrl = this.environmentService.getRescueiOSStoreUrl();
		this.androidStoreUrl = this.environmentService.getRescueAndroidStoreUrl();
		this.validateUpgradeCode();
	}

	ngOnDestroy(): void {
		this.dropInInstance?.teardown(() => { });
	}

	get form() { return this.paymentForm.controls; }

	/** Same pre-check the realtor page runs: refuses a used code or a plan already pending. */
	private validateUpgradeCode(): void {
		const url = `${this.environmentService.getIA9LightningUrl()}V3/payment/update/${this.upgradeCode}?code=${this.environmentService.getIA9LightningKey()}`;
		this.spinner.show();
		this.realtorService.validateCode(url, this.xClientId).subscribe(
			() => this.retrievePlans(),
			(error) => this.showFailure(error, false));
	}

	/**
	 * The plans route resolves the catalogue from the code (Plan.cs maps a consumer's
	 * UpgradeMembership code to ConsumerUpgrade), so the path segment is only a fallback.
	 */
	private retrievePlans(): void {
		const url = `${this.environmentService.getIA9LightningUrl()}payment/consumerupgrade/plans?pcode=${this.upgradeCode}&code=${this.environmentService.getIA9LightningKey()}`;
		this.professionalService.getMembershipPlansRealtors(url, this.xClientId).subscribe(
			(response) => {
				this.spinner.hide();
				this.plans = (response?.data ?? [])
					.filter((plan: RealtorPlan) => !this.isFree(plan.price) && !plan.currentPlanStatus);

				if (this.plans.length === 0) {
					this.errorMessage = 'There is no RescueMII membership available right now. Please try again later.';
					this.errorHasButton = false;
					document.getElementById('consumerFailedBtn').click();
					return;
				}
				// One plan today; pre-select it so the radio reflects what Submit will buy.
				this.selectPlan(this.plans[0]);
			},
			(error) => this.showFailure(error, false));
	}

	selectPlan(plan: RealtorPlan): void {
		this.selectedPlan = plan;
		this.paymentForm.get('membershipPlan').setValue(plan.code);
	}

	/** The "Credit Card" control opens Braintree Drop-in; ticking it again closes it. */
	toggleCardForm(): void {
		if (this.isCardFormOpen) {
			this.closeCardForm();
			return;
		}
		this.spinner.show();
		const url = this.environmentService.getIA9LightningUrl() + 'registration/getpaymenttoken';
		this.professionalService.getClientAuthToken(url, this.xClientId).subscribe(
			(response) => {
				if (!response?.data) {
					this.showFailure(null, true);
					return;
				}
				this.isCardFormOpen = true;
				// The container renders under *ngIf, so wait a tick for it to exist.
				setTimeout(() => {
					dropin.create({
						authorization: response.data,
						container: document.getElementById('consumer-dropin-container')
					}).then((instance) => {
						this.dropInInstance = instance;
						this.paymentForm.get('creditCard').setValue(true);
						this.spinner.hide();
					}).catch((error) => {
						console.error(error);
						this.closeCardForm();
						this.showFailure(null, true);
					});
				}, 0);
			},
			(error) => this.showFailure(error, true));
	}

	private closeCardForm(): void {
		this.dropInInstance?.teardown(() => { });
		this.dropInInstance = null;
		this.isCardFormOpen = false;
		this.paymentForm.get('creditCard').setValue(false);
	}

	onSubmit(): void {
		this.submitted = true;
		if (this.paymentForm.invalid || !this.selectedPlan || !this.dropInInstance) {
			Object.values(this.paymentForm.controls).forEach(c => c.markAsTouched());
			return;
		}

		this.dropInInstance.requestPaymentMethod()
			.then((payload) => this.pay(payload.nonce))
			.catch((error) => console.error(error)); // Drop-in shows its own field errors.
	}

	private pay(nonce: string): void {
		const body = new PaymentModel();
		body.paymentMethodToken = nonce;
		body.membershipPlanCode = this.selectedPlan.code;
		body.membershipPlanId = this.selectedPlan.uid;
		body.code = this.upgradeCode;
		body.promoCode = '';
		body.ZipCodes = '';

		const url = `${this.environmentService.getIA9LightningUrl()}V3/payment/update?code=${this.environmentService.getIA9LightningKey()}`;
		this.spinner.show();
		this.realtorService.realtorPayment(url, body, this.xClientId).subscribe(
			() => {
				this.spinner.hide();
				this.successMessage = this.fromSignup
					? 'Your RescueMII™ membership is active. Download the RescueMII app and sign in to get started.'
					: 'Your RescueMII™ membership is active. Return to the RescueMII app to continue.';
				document.getElementById('consumerSuccessBtn').click();
			},
			(error) => this.showFailure(error, true));
	}

	private showFailure(error: any, canRetry: boolean): void {
		this.spinner.hide();
		this.errorMessage = error?.error?.description
			|| 'We are sorry, your request could not be processed. Please try again.';
		this.errorHasButton = canRetry;
		// 6001 = the code was already used, i.e. this membership was already bought.
		if (error?.error?.statusCode === 6001) {
			this.errorMessage = 'Your RescueMII™ membership is already active.<br />You may now close this page.';
			this.errorHasButton = false;
		}
		document.getElementById('consumerFailedBtn').click();
	}

	openTerms(event: Event, type: 'TC' | 'PP'): void {
		event.preventDefault();
		document.getElementById('consumerTermsBtn').click();
		(document.getElementById('consumerTcLogo') as HTMLImageElement).src =
			CommonConstants.imageBasePath + this.environmentService.getCompanyLogo();
		this.tcHeader = type === 'TC' ? 'Terms and Conditions' : 'Privacy Policy';
		const url = type === 'TC' ? this.environmentService.getEULAUrl() : this.environmentService.getPrivacyUrl();
		this.tcURL = this.sanitizer.bypassSecurityTrustResourceUrl(url);
	}

	clearTermsUrl(): void { this.tcURL = null; }

	/** Signup only: keep the free plan the account already has and go get the app. */
	continueFree(): void {
		this.successMessage = 'Your RescueMII™ account is ready. Download the RescueMII app and sign in. '
			+ 'You can upgrade any time from the link in your welcome email.';
		document.getElementById('consumerSuccessBtn').click();
	}

	openAppStore(): void { window.open(this.iOSStoreUrl); }

	openPlayStore(): void { window.open(this.androidStoreUrl); }

	private isFree(price: string): boolean {
		const p = (price ?? '').toString().trim().toLowerCase();
		return p === '' || p === 'free' || Number(p.replace('$', '')) === 0;
	}
}
